import { Box, Container, Typography, Dialog, DialogContent, IconButton, Chip, Stack, Grid, Tabs, Tab, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import Kundenbewertungen from "./Kundenbewertungen";
import { useTranslation } from "react-i18next";

// Dummy-Daten: Wir gruppieren die 44 Bilder in fiktive Projekte
const projectsData = Array.from({ length: 12 }, (_, i) => {
  const projectId = i + 1;
  const imageCount = (i % 3) + 3; 
  const startIndex = (i * 3) + 1;
  
  const images = Array.from({ length: imageCount }, (_, imgIndex) => {
    const num = Math.min(((startIndex + imgIndex) % 44) + 1, 44);
    return `/assets/projects/projekt-${num}.jpg`;
  });

  return {
    id: projectId,
    title: `Solarprojekt ${projectId}`,
    location: ["Heilbronn", "Stuttgart", "Karlsruhe", "Ludwigsburg", "Pforzheim"][i % 5],
    mainImage: images[0],
    gallery: images,
    description: "Für dieses Projekt haben wir eine maßgeschneiderte Photovoltaikanlage geplant und installiert. Der Fokus lag auf maximaler Eigenverbrauchsquote und einer ästhetischen Integration in das Dachbild. Die Anlage läuft einwandfrei und der Kunde spart vom ersten Tag an Stromkosten.",
    details: [
      { label: "Anlagengröße", value: `${(8 + (i % 5) * 2.5).toFixed(1)} kWp` },
      { label: "Speicher", value: `${(5 + (i % 3) * 2.5).toFixed(1)} kWh` },
      { label: "Module", value: "Premium Glas-Glas Module" },
      { label: "Dauer", value: "2 Tage Montage" },
    ]
  };
});

export default function Referenzen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  
  const [selectedProject, setSelectedProject] = useState<typeof projectsData[0] | null>(null);
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState(0);

  const handleOpen = (project: typeof projectsData[0]) => {
    setSelectedProject(project);
    setActiveImage(project.mainImage);
  };
  
  const handleClose = () => {
    setSelectedProject(null);
    setActiveImage(null);
  };

  const reviewsContent = (
    <Box sx={{ position: { lg: "sticky" }, top: { lg: 100 } }}>
       <Box className="section-soft" sx={{ mb: 4 }}>
         <Typography variant="h3" sx={{ mb: 1 }}>
           {t("referencesPage.reviewsTitle")}
         </Typography>
         <Typography variant="body2" color="text.secondary">
           {t("referencesPage.reviewsSubtitle")}
         </Typography>
       </Box>
       
       <Box sx={{ 
         maxHeight: { lg: "calc(100vh - 250px)" }, 
         overflowY: { lg: "auto" }, 
         pr: { lg: 2 },
         "&::-webkit-scrollbar": { width: "6px" },
         "&::-webkit-scrollbar-track": { background: "transparent" },
         "&::-webkit-scrollbar-thumb": { background: "var(--border)", borderRadius: "3px" },
         "&::-webkit-scrollbar-thumb:hover": { background: "var(--accent)" }
       }}>
          <Kundenbewertungen asPage={false} layout="stack" />
       </Box>
    </Box>
  );

  const projectsContent = (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          {t("referencesPage.projectsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("referencesPage.projectsSubtitle")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)"
          },
          gap: 3,
        }}
      >
        {projectsData.map((project) => (
          <Box 
            key={project.id} 
            onClick={() => handleOpen(project)}
            sx={{ 
              borderRadius: 3, 
              overflow: "hidden", 
              bgcolor: "var(--surface)",
              border: "1px solid var(--border)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": { 
                transform: "translateY(-4px)",
                borderColor: "var(--accent)",
                boxShadow: "0 12px 24px -12px rgba(0,0,0,0.2)"
              }
            }}
          >
            <Box
              component="img"
              src={project.mainImage}
              alt={project.title}
              loading="lazy"
              sx={{
                width: "100%",
                height: 200,
                objectFit: "cover",
                display: "block"
              }}
            />
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {project.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {project.location}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ pb: { xs: 6, md: 10 } }}>
      {/* Hero Header */}
      <Container sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 4 } }}>
        <Box className="hero-shell" sx={{ p: { xs: 4, md: 6 }, position: "relative" }}>
          <Box className="hero-glow" />
          <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1, maxWidth: 820 }}>
            <Typography variant="h1">{t("home.testimonials.title")}</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {t("referencesPage.heroSubtitle")}
            </Typography>
          </Stack>
        </Box>
      </Container>
      
      <Container maxWidth="xl">
        {isDesktop ? (
          /* Split-Screen für Desktop (lg und größer) */
          <Grid container spacing={6}>
            <Grid item xs={12} lg={5}>{reviewsContent}</Grid>
            <Grid item xs={12} lg={7}>{projectsContent}</Grid>
          </Grid>
        ) : (
          /* Tabs für Mobile/Tablet (kleiner als lg) */
          <Box>
            <Tabs 
              value={mobileTab} 
              onChange={(_, newValue) => setMobileTab(newValue)} 
              variant="fullWidth"
              sx={{ mb: 4, borderBottom: "1px solid var(--border)" }}
            >
              <Tab label={t("referencesPage.tabReviews")} sx={{ fontWeight: 600, py: 2 }} />
              <Tab label={t("referencesPage.tabProjects")} sx={{ fontWeight: 600, py: 2 }} />
            </Tabs>
            
            <Box sx={{ pt: 1 }}>
              {mobileTab === 0 && reviewsContent}
              {mobileTab === 1 && projectsContent}
            </Box>
          </Box>
        )}
      </Container>

      {/* Detail-Modal */}
      <Dialog 
        open={Boolean(selectedProject)} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: "var(--surface)" } }}
      >
        {selectedProject && (
          <>
            <IconButton 
              onClick={handleClose} 
              sx={{ position: "absolute", right: 8, top: 8, color: "white", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" }, zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>
            
            <Box sx={{ p: 0 }}>
              <Box
                component="img"
                src={activeImage || selectedProject.mainImage}
                sx={{ width: "100%", height: { xs: 250, md: 400 }, objectFit: "cover", display: "block", transition: "opacity 0.2s ease" }}
              />
            </Box>

            <DialogContent sx={{ p: { xs: 3, md: 5 } }}>
              <Typography variant="h3" gutterBottom>
                {selectedProject.title}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {t("referencesPage.modalLocation")} {selectedProject.location}
              </Typography>

              <Typography variant="body1" sx={{ mt: 3, mb: 4, lineHeight: 1.7 }}>
                {selectedProject.description}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontSize: "1.1rem" }}>
                {t("referencesPage.modalTechDetails")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 5 }}>
                {selectedProject.details.map((detail, idx) => (
                  <Chip 
                    key={idx} 
                    label={`${detail.label}: ${detail.value}`} 
                    sx={{ bgcolor: "var(--surface-sunken)", border: "1px solid var(--border)", borderRadius: 1.5 }}
                  />
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontSize: "1.1rem" }}>
                {t("referencesPage.modalGallery")}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 2 }}>
                {selectedProject.gallery.map((img, idx) => (
                  <Box
                    key={idx}
                    component="img"
                    src={img}
                    loading="lazy"
                    onClick={() => setActiveImage(img)}
                    sx={{ 
                      width: "100%", 
                      height: 120, 
                      objectFit: "cover", 
                      borderRadius: 2,
                      cursor: "pointer",
                      border: activeImage === img ? "2px solid var(--accent)" : "2px solid transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                         opacity: 0.8
                      }
                    }}
                  />
                ))}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
